import _ from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { Output, EventEmitter, Injectable } from '@angular/core';
import { Team } from 'src/app/shared/models/team/team';
import { Education } from 'src/app/people/models/education';
import { Person } from 'src/app/people/models/person';
import { Image } from 'src/app/people/models/image';
import { TeamHistory } from 'src/app/people/models/teamHistory';
import { PersonDetailsWrite } from 'src/app/people/models/personDetailsWrite';
import { HistoricalTeamWrite } from 'src/app/shared/models/team/historicalTeamWrite';
import { HistoricalTeam } from 'src/app/shared/models/team/historicalTeam';
import { map, publishLast, catchError, refCount } from 'rxjs/operators';
import { PersonApplication } from 'src/app/shared/models/application/personApplication';
import { Certification } from 'src/app/people/models/certification';
import {
  PersonCertificationComparator,
  StringIgnoreCaseComparator
} from 'src/app/shared/utils/comparators';
import { BaseService } from 'src/app/shared/services/base.service';
import { ApiVersions } from 'src/app/shared/services/api-version';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { PersonDisclaimer } from 'src/app/people/models/personDisclaimer';
import { PersonCertification } from 'src/app/people/models/personCertification';
import { PersonCertificationWrite } from 'src/app/people/models/personCertificationWrite';
import { Note } from 'src/app/people/models/note';
import { PersonEvaluationSummary } from '../models/evaluation/personEvaluationSummary';
import { PersonEvaluationResponsesDetail } from '../models/evaluation/personEvaluationResponsesDetail';
import { EvaluationFilterResult } from '../models/evaluation/evaluationFilterResult';
import { MergeProfileService } from 'src/app/people/merge-profile/merge-profile.service';
import { MergeProfileResult } from 'src/app/people/merge-profile/merge-profile-result';
import { InjectorModule } from '../modules/injector.module';
import { EvaluationAggregateFilterResult } from '../models/evaluation/evaluationAggregateFilterResult';
import {
  IPersonEvaluationQuestionOverview,
  PersonEvaluationOverview
} from '../models/evaluation/personEvaluationOverview';
import { ModalConfirmationComponent } from 'src/shared-app-portal/components/modal-confirmation/modal-confirmation.component';
import { nameOf } from 'src/app/shared/utils/name-of';
import { IEvaluationQueryModel } from 'src/app/shared/models/evaluation/evaluationQueryModel';
import { IPeople } from 'src/app/people/models/people.model';
import { Seat } from 'src/app/shared/models/team/seat';
import { downloadFile } from 'src/app/shared/utils/download-file-base64';

@Injectable()
export class PersonDetailsService extends BaseService {
  private baseCertificationsPath: string = 'certifications';
  private baseUsersPath: string = 'users';
  public readonly personFetched = new Subject<Person>();
  public readonly personUpdated = new Subject<Person>();
  public readonly personCreated = new Subject<Person>();
  public readonly personDeleted = new Subject<Person>();
  public readonly personTeamHistoryUpdated = new Subject<void>();
  public readonly personTeamSeats = new BehaviorSubject<Seat[]>([]);

  @Output() closePersonDetailsEvent = new EventEmitter<void>();
  @Output() teamOverlapsEvent = new EventEmitter<Team[]>();

  public isLoading = true;
  public teamCount = 0;

  public certifications = new Subject<Certification[]>();
  private mergeProfileService: MergeProfileService;
  constructor() {
    super();
    this.mergeProfileService = InjectorModule.injector.get(MergeProfileService);
    this.mergeProfileService.mergeResult.subscribe((mergeResult: MergeProfileResult) => {
      if (mergeResult && mergeResult.mergedProfile) {
        if (this._person.id == mergeResult.mergedProfile.id) {
          this.getPerson(mergeResult.mergedProfile.id);
        }
        this.personDeleted.next(mergeResult.deletedProfile);
      }
    });
  }

  protected _person: Person;

  public get currentPerson(): Person {
    return this._person;
  }

  getPerson(id: string, ok404: boolean = true) {
    this._person = null;
    this.isLoading = true;
    this.get(this.usersBaseUrl(id), ok404, ApiVersions.v1_0)
      .pipe(map(this.convertPerson))
      .subscribe((person) => {
        this.isLoading = false;
        if (person) {
          this._person = person;
          this.personFetched.next(this._person);
          this.getPersonDetails();
        } else {
          this.personFetched.error({ message: 'Person not found', status: 404 });
        }
      });
  }

  setPerson(value: Person) {
    if (!this._person) {
      this._person = value;
    }
  }

  addTeamHistory(newTeamHistory: TeamHistory) {
    this._person.addTeamHistory(newTeamHistory);
    this.personUpdated.next(this._person);
    this.personTeamHistoryUpdated.next();
  }
  updateTeamHistory(newTeamHistory: TeamHistory) {
    this._person.updateTeamHistory(newTeamHistory);
    this.personUpdated.next(this._person);
    this.personTeamHistoryUpdated.next();
  }

  deleteTeamHistory(historicalTeamId: string) {
    this._person.deleteTeamHistory(historicalTeamId);
    this.personUpdated.next(this._person);
    this.personTeamHistoryUpdated.next();
  }
  getPersonDetails() {
    this.getPersonEducation(this._person.id).subscribe((result) => {
      if (this._person) {
        this._person.setEducation(result);
      }
    });
    this.getPersonTeamsHistory(this._person.id).subscribe((result) => {
      if (this._person) {
        this._person.setTeamHistory(result);
        this.personTeamHistoryUpdated.next();
      }
    });
    this.getPersonCertifications(this._person.id).subscribe((result) => {
      if (this._person) {
        this._person.setCertifications(result);
      }
    });
    if (this._person?.demographicInfo) {
      this._person.demographicInfo.races = _.sortBy(
        this._person.demographicInfo.races,
        (r) => r.name
      );
    }
  }
  getPersonDisclaimers(userId: string): Observable<PersonDisclaimer[]> {
    return this.get(this.getPersonDisclaimersUrl(userId), true, ApiVersions.v1_0)
      .pipe(publishLast())
      .pipe(refCount());
  }

  getPersonEducation(userId: string): Observable<Education[]> {
    return this.get(this.getPersonEducationUrl(userId), true)
      .pipe(map(this.convertEducationData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getEvaluationsSummary(userId: string): Observable<PersonEvaluationSummary[]> {
    return this.get(this.getEvaluationsSummaryUrl(userId), true)
      .pipe(map(this.convertEvaluationsSummaryData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getPersonWorkspaceRatingAggregate(userId: string, workspaceId: string): Observable<number> {
    var url = this.getEvaluationsSummaryUrl(userId)
      .concat(`/${workspaceId}`)
      .concat('/workspace-rating');
    return this.get(url, true);
  }

  getEvaluationResponsesDetail(
    userId: string,
    evaluationFormId: string
  ): Observable<PersonEvaluationResponsesDetail> {
    return this.get(
      this.getEvaluationResponsesDetailUrl(userId, evaluationFormId),
      true,
      ApiVersions.unversioned
    )
      .pipe(map(this.convertEvaluationResponseDetailData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getEvaluationOverview(userId: string): Observable<IPersonEvaluationQuestionOverview[]> {
    return this.get(this.getEvaluationOverviewUrl(userId), true, ApiVersions.v1_0)
      .pipe(map(this.convertEvaluationOverviewData))
      .pipe(publishLast())
      .pipe(refCount());
  }
  getApplications(userId: string): Observable<PersonApplication[]> {
    return this.get(this.getApplicationsUrl(userId), true)
      .pipe(map(this.convertApplications))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getPersonTeamsHistory(userId: string): Observable<TeamHistory[]> {
    return this.get(this.getPersonTeamHistoryUrl(userId), true, ApiVersions.v1_0)
      .pipe(map(this.convertTeamHistoryData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getCurrentPersonTeamsHistory(userId: string): Observable<TeamHistory[]> {
    return this.get(this.getCurrentPersonTeamHistoryUrl(userId), true, ApiVersions.v1_0)
      .pipe(map(this.convertTeamHistoryData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getUpcomingPersonTeamsHistory(userId: string): Observable<TeamHistory[]> {
    return this.get(this.getUpcomingPersonTeamHistoryUrl(userId), true, ApiVersions.v1_0)
      .pipe(map(this.convertTeamHistoryData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  getCertifications(): Observable<Certification[]> {
    let sub = this.get(this.baseCertificationsPath, true)
      .pipe(map(this.convertCertificationsData))
      .pipe(map(this.orderCertifications))
      .pipe(publishLast())
      .pipe(refCount());
    sub.subscribe((result) => this.certifications.next(result));
    return sub;
  }
  getPersonCertifications(userId: string): Observable<PersonCertification[]> {
    return this.get(this.getPersonCertificationsUrl(userId), false, ApiVersions.v1_0)
      .pipe(map(this.convertPersonCertificationsData))
      .pipe(map(this.orderPersonCertifications))
      .pipe(publishLast())
      .pipe(refCount());
  }

  deletePersonCertification(userId: string, certificationId: string) {
    return this.delete(
      this.getPersonCertificationEditDeleteUrl(userId, certificationId),
      null,
      ApiVersions.v1_0
    );
  }

  getPersonCertification(userId: string, certificationId: string): Observable<PersonCertification> {
    return this.get(
      this.getPersonCertificationEditDeleteUrl(userId, certificationId),
      false,
      ApiVersions.v1_0
    )
      .pipe(map(this.convertPersonCertificationData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  savePersonCertification(
    userId: string,
    personCertificationId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    return personCertificationId
      ? this.updatePersonCertification(userId, personCertificationId, personCertification)
      : this.createPersonCertification(userId, personCertification);
  }

  createPersonHistoricalTeam(
    userId: string,
    historicalTeamWrite: HistoricalTeamWrite
  ): Observable<HistoricalTeam> {
    let historicalTeamWriteStream = JSON.stringify(historicalTeamWrite);
    return this.post(this.getHistoricalTeamsUrl(userId), historicalTeamWriteStream)
      .pipe(map(this.convertHistoricalTeamData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  editPersonHistoricalTeam(
    userId: string,
    historicalTeamId: string,
    historicalTeamWrite: HistoricalTeamWrite
  ): Observable<HistoricalTeam> {
    let historicalTeamWriteStream = JSON.stringify(historicalTeamWrite);
    return this.put(
      this.getHistoricalTeamsEditDeleteUrl(userId, historicalTeamId),
      historicalTeamWriteStream
    )
      .pipe(map(this.convertHistoricalTeamData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  deletePersonHistoricalTeam(userId: string, historicalTeamId: string) {
    return this.delete(this.getHistoricalTeamsEditDeleteUrl(userId, historicalTeamId));
  }

  createPersonDetails(personDetailsWrite: PersonDetailsWrite): Promise<Person> {
    this.isLoading = true;
    let body = JSON.stringify(personDetailsWrite);
    return lastValueFrom(
      this.post(this.baseUsersPath, body, null, ApiVersions.v1_0, true)
        .pipe(catchError((err) => this.handleDublicateEmailInOtherWorkspaceError(err)))
        .pipe(map(this.convertPerson))
        .pipe(publishLast())
        .pipe(refCount())
    ).then((person: Person) => {
      this.isLoading = false;
      this._person = person;
      this.personCreated.next(this._person);
      return this._person;
    });
  }

  updatePersonDetails(userId: string, personDetailsWrite: PersonDetailsWrite): Observable<Person> {
    let body = JSON.stringify(personDetailsWrite);
    let obs = this.put(this.usersBaseUrl(userId), body, null, ApiVersions.v1_0, true)
      .pipe(catchError((err) => this.handleDublicateEmailInOtherWorkspaceError(err)))
      .pipe(map(this.convertPerson))
      .pipe(publishLast())
      .pipe(refCount());

    // Updating a person can add or remove tags from the list of valid tags,
    // so update the list of cached tags.
    obs.subscribe((updatedPerson) => {
      if (updatedPerson) {
        this._person.update(updatedPerson);
        this.personUpdated.next(this._person);
        this.getPersonDetails();
      }
    });

    return obs;
  }

  patchPersonMetadata(personId: string, extSysId: string) {
    let path = `${this.usersBaseUrl(personId)}/metadata`;
    this.patch(path, { externalSystemId: extSysId }, ApiVersions.v1_0).subscribe((result) => {
      if (result) {
        this._person.update(result);
        this.personUpdated.next(this._person);
        this.openSnackBar(
          `Successfully updated ${this._person.fullName}'s external system id.`,
          true
        );
        this.getPersonDetails();
      }
    });
  }

  setAttribute(attributeDefinitionId: string, attributes: Attribute[]) {
    this._person.setAttribute(attributeDefinitionId, attributes);
    this.personUpdated.next(this._person);
  }

  setStatus(userId: string, status: string, workspaceId: string = null): Observable<Person> {
    let path = `${this.usersBaseUrl(userId)}/status?status=${status}`;
    if (workspaceId) {
      path += `&workspaceId=${workspaceId}`;
    }
    let obs = this.patch(path).pipe(map(this.convertPerson));

    obs.subscribe((updatedPerson) => {
      if (!updatedPerson) return;
      this.openSnackBar(`Successfully changed ${updatedPerson.fullName}'s status`, true);
      if (this._person) {
        this._person.update(updatedPerson);
        this.personUpdated.next(this._person);
      }
    });

    return obs;
  }

  setOrgAndWorkspaceStatus(userId: string, status: string): Observable<Person> {
    let path = `${this.usersBaseUrl(userId)}/orgAndWorkspaceStatus?status=${status}`;
    let obs = this.patch(path).pipe(map(this.convertPerson));

    obs.subscribe((updatedPerson) => {
      if (!updatedPerson) return;
      this.openSnackBar(`Successfully changed ${updatedPerson.fullName}'s status`, true);
      if (this._person) {
        this._person.update(updatedPerson);
        this.personUpdated.next(this._person);
      }
    });

    return obs;
  }

  deletePerson(person: Person) {
    this.delete(this.usersBaseUrl(person.id), null, ApiVersions.v0_1, true).subscribe(
      () => {
        this.openSnackBar(`Successfully deleted ${person.fullName}.`, true);
        this.personDeleted.next(person);
        this.closePersonDetailsEvent.emit();
      },
      (error) => {
        if (error?.status == 400 && error?.error) {
          this.openSnackBar(`${error.error}`);
        } else {
          this.openSnackBar(`Could not delete person.`);
        }
      }
    );
  }

  patchPersonImageId(userId: string, imageId: string) {
    this.patch(this.getPersonImageUrl(userId), { imageId: imageId }).subscribe(() => {
      this._person.image = new Image(imageId);
      this.personUpdated.next(this._person);
    });
  }

  patchRating(userId: string) {
    this.patch(this.getPersonRatingUrl(userId)).subscribe(() => {
      this.personUpdated.next(this._person);
    });
  }

  deleteForm(userId: string, applicationId: string): Observable<any> {
    return this.delete(
      `${this.baseUsersPath}/${userId}/applications/${applicationId}`,
      null,
      ApiVersions.v0_1,
      true
    );
  }

  exportFormResponse(userId: string, applicationId: string) {
    return lastValueFrom(
      this.get(
        `${this.baseUsersPath}/${userId}/applications/${applicationId}/export-pdf`,
        true,
        ApiVersions.v0_1,
        true
      )
    ).then(
      (result) => {
        if (result) {
          downloadFile(result);
        }
      },
      (error) => {
        this.openSnackBar(`Error downloading Form Response PDF ${error?.error}`, false);
      }
    );
  }

  sendSingleApplication(userId: string, formId: string): Observable<any> {
    return this.post(
      `${this.baseUsersPath}/${userId}/applications/${formId}/assign`,
      null,
      null,
      ApiVersions.v1_0
    );
  }

  resetTeamHistory() {
    if (!this._person) {
      return;
    }
    this.getPersonTeamsHistory(this._person.id).subscribe((teamHistory) => {
      this._person.setTeamHistory(teamHistory);
      this.personTeamHistoryUpdated.next();
    });
  }

  resetPersonCertifications() {
    this.getPersonCertifications(this._person.id).subscribe((certifications) => {
      this._person.setCertifications(certifications);
    });
  }

  getPersonTeamSeats(personId: string, teamId: string) {
    return this.get(this.getPersonTeamSeatsUrl(personId, teamId), true, ApiVersions.v1_0)
      .pipe(map(this.convertTeamSeatData))
      .subscribe((data) => {
        if (data) {
          this.personTeamSeats.next(data);
        }
      });
  }

  private handleDublicateEmailInOtherWorkspaceError(errorResponse: HttpErrorResponse) {
    if (
      errorResponse.error &&
      errorResponse.status === 400 &&
      errorResponse.error['emailInOtherWorkspace']
    ) {
      this.dialog.open(ModalConfirmationComponent, {
        data: {
          simpleDialog: true,
          title: 'Unable to add this email address to your workspace.',
          description: errorResponse.error['emailInOtherWorkspace'],
          okButtonText: 'Got it!'
        }
      });
      return of(null);
    } else {
      this.handleError(errorResponse);
      return of(null);
    }
  }

  private createPersonCertification(
    userId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    let body = JSON.stringify(personCertification);
    return this.post(this.getPersonCertificationsUrl(userId), body, null, ApiVersions.v1_0)
      .pipe(map(this.convertPersonCertificationData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  private updatePersonCertification(
    userId: string,
    personCertificationId: string,
    personCertification: PersonCertificationWrite
  ): Observable<PersonCertification> {
    let body = JSON.stringify(personCertification);
    return this.put(
      this.getPersonCertificationEditDeleteUrl(userId, personCertificationId),
      body,
      null,
      ApiVersions.v1_0
    )
      .pipe(map(this.convertPersonCertificationData))
      .pipe(publishLast())
      .pipe(refCount());
  }

  private getPersonCertificationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/certifications');
  }

  private getPersonCertificationEditDeleteUrl(userId: string, certificationId: string) {
    return this.getPersonCertificationsUrl(userId).concat(`/${certificationId}`);
  }

  private convertPersonCertificationsData(data) {
    return _.map(data, function (personCertification) {
      return new PersonCertification(personCertification);
    });
  }

  private convertPersonCertificationData(data) {
    return new PersonCertification(data);
  }

  private orderPersonCertifications(data) {
    return data.sort(PersonCertificationComparator);
  }

  private convertPersonNoteData(data) {
    return new Note(data);
  }

  private getPersonDisclaimersUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/disclaimers');
  }

  private getPersonEducationUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/educations');
  }

  private getEvaluationsSummaryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/evaluations');
  }

  private getEvaluationResponsesDetailUrl(userId: string, evaluationFormId) {
    const filter = `$filter=${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateeId
    )} eq ${userId} and ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationFormId
    )} eq ${evaluationFormId} and ${nameOf<IEvaluationQueryModel>((e) => e.responses)}/any()`;
    const select = `$select=${nameOf<IEvaluationQueryModel>(
      (e) => e.teamName
    )}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluateePosition
    )}, ${nameOf<IEvaluationQueryModel>((e) => e.evaluationName)}, ${nameOf<IEvaluationQueryModel>(
      (e) => e.evaluationResponseDate
    )}`;
    const expand = `$expand=${nameOf<IEvaluationQueryModel>((e) => e.responses)}`;
    return `query/evaluationMaxResult?${filter}&${select}&${expand}&$count=true`;
  }

  private getEvaluationOverviewUrl(userId: string) {
    const filter = `$filter=${nameOf<IPeople>((e) => e.id)} eq ${userId}`;
    const select = `$select=${nameOf<IPeople>((e) => e.evaluationAggregates)}`;
    const expand = `$expand=${nameOf<IPeople>((e) => e.evaluationAggregates)}`;
    return `query/people?${filter}&${select}&${expand}`;
  }
  private getApplicationsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/applications');
  }

  private getPersonImageUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/image');
  }

  private getPersonRatingUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/update-rating');
  }

  private getPersonTeamHistoryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/teams');
  }

  private getCurrentPersonTeamHistoryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/current-teams');
  }

  private getUpcomingPersonTeamHistoryUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/upcoming-teams');
  }

  private getHistoricalTeamsUrl(userId: string) {
    return this.usersBaseUrl(userId).concat('/historicalTeams');
  }

  private getHistoricalTeamsEditDeleteUrl(userId: string, historicalTeamId: string) {
    return this.getHistoricalTeamsUrl(userId).concat(`/${historicalTeamId}`);
  }

  private getPersonTeamSeatsUrl(userId: string, teamId: string) {
    return this.usersBaseUrl(userId).concat('/seats/').concat(teamId);
  }

  private usersBaseUrl(userId: string) {
    return `${this.baseUsersPath}/${userId}`;
  }

  private convertEducationData(data) {
    return _.map(data, function (education) {
      return new Education(education);
    });
  }

  private convertEvaluationsSummaryData(data) {
    return _.map(data, function (evaluationSummary) {
      return PersonEvaluationSummary.parse(evaluationSummary);
    });
  }

  private convertEvaluationResponseDetailData(data) {
    if (data) {
      let evaluationODataResult = new EvaluationFilterResult().parse(data);
      return PersonEvaluationResponsesDetail.parse(evaluationODataResult);
    }
  }

  private convertEvaluationOverviewData(data) {
    if (data) {
      let evaluationAggregateFilterResult = new EvaluationAggregateFilterResult().parse(data);
      return PersonEvaluationOverview.parse(evaluationAggregateFilterResult.values);
    }
  }

  private convertApplications(data): PersonApplication[] {
    return _.map(data, function (application) {
      return PersonApplication.parse(application);
    });
  }

  private convertTeamHistoryData(data) {
    return _.map(data, function (teamHistory) {
      return new TeamHistory(teamHistory);
    });
  }

  private convertCertificationsData(data) {
    return _.map(data, function (certification) {
      return new Certification(certification);
    });
  }

  private orderCertifications(data) {
    return data.sort(StringIgnoreCaseComparator('name'));
  }

  private convertHistoricalTeamData(data) {
    return new HistoricalTeam(data);
  }

  private convertPerson(data): Person {
    if (!data) return null;
    return new Person(data);
  }

  private convertTeamSeatData(data): Seat[] {
    return data.map((seat) => Seat.parse(seat));
  }
}
